import React from 'react';

// Helpers para valores seguros
export const safe = <T,>(value: T | null | undefined, fallback: T): T => {
  return value ?? fallback;
};

export const safeString = (value: string | null | undefined, fallback = ''): string => {
  return typeof value === 'string' ? value : fallback;
};

export const safeNumber = (value: number | null | undefined, fallback = 0): number => {
  return typeof value === 'number' && !isNaN(value) ? value : fallback;
};

export const safeBoolean = (value: boolean | null | undefined, fallback = false): boolean => {
  return typeof value === 'boolean' ? value : fallback;
};

export const safeArray = <T,>(arr?: T[] | null): T[] => {
  return Array.isArray(arr) ? arr : [];
};

export const safeObject = <T extends Record<string, any>>(obj?: T | null, fallback = {} as T): T => {
  return obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : fallback;
};

// Helpers para renderização condicional
export const SafeRender: React.FC<{
  data: any;
  fallback?: React.ReactNode;
  children: (data: any) => React.ReactNode;
}> = ({ data, fallback = null, children }) => {
  if (data == null) {
    return <>{fallback}</>;
  }
  
  try {
    return <>{children(data)}</>;
  } catch (error) {
    console.error('SafeRender error:', error);
    return <>{fallback}</>;
  }
};

export const SafeList: React.FC<{
  items?: any[];
  fallback?: React.ReactNode;
  children: (item: any, index: number) => React.ReactNode;
  keyExtractor?: (item: any, index: number) => string | number;
}> = ({ 
  items, 
  fallback = <div className="text-muted-foreground text-center py-4">Nenhum item encontrado</div>,
  children,
  keyExtractor = (_, index) => index
}) => {
  const safeItems = safeArray(items);
  
  if (safeItems.length === 0) {
    return <>{fallback}</>;
  }
  
  try {
    return (
      <>
        {safeItems.map((item, index) => (
          <React.Fragment key={keyExtractor(item, index)}>
            {children(item, index)}
          </React.Fragment>
        ))}
      </>
    );
  } catch (error) {
    console.error('SafeList error:', error);
    return <>{fallback}</>;
  }
};

// Componente para estados de loading
export const LoadingState: React.FC<{
  isLoading?: boolean;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ 
  isLoading = false, 
  children, 
  fallback = (
    <div className="flex items-center justify-center p-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      <span className="ml-2 text-muted-foreground">Carregando...</span>
    </div>
  )
}) => {
  if (isLoading) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
};

// Componente para estados de erro
export const ErrorState: React.FC<{
  error?: Error | null;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onRetry?: () => void;
}> = ({ 
  error, 
  children, 
  fallback,
  onRetry 
}) => {
  if (!error) {
    return <>{children}</>;
  }
  
  if (fallback) {
    return <>{fallback}</>;
  }
  
  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
        <svg className="w-6 h-6 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-foreground text-foreground mb-2">Algo deu errado</h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-md">
        {error.message || 'Ocorreu um erro inesperado. Tente novamente.'}
      </p>
      {onRetry && (
        <button 
          onClick={onRetry}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          Tentar novamente
        </button>
      )}
    </div>
  );
};

// Componente para estados vazios
export const EmptyState: React.FC<{
  isEmpty?: boolean;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
}> = ({ 
  isEmpty = false, 
  children, 
  fallback,
  title = "Nenhum item encontrado",
  description = "Não há dados para exibir no momento.",
  icon
}) => {
  if (!isEmpty) {
    return <>{children}</>;
  }
  
  if (fallback) {
    return <>{fallback}</>;
  }
  
  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      {icon && <div className="mb-4">{icon}</div>}
      <h3 className="text-lg font-semibold text-foreground text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
};

// HOC para envolver componentes com proteções
export const withSafeRender = <P extends object>(
  Component: React.ComponentType<P>,
  options: {
    fallback?: React.ReactNode;
    errorFallback?: React.ReactNode;
    loadingFallback?: React.ReactNode;
  } = {}
) => {
  const WrappedComponent: React.FC<P> = (props) => {
    try {
      return <Component {...props} />;
    } catch (error) {
      console.error('Component error caught by withSafeRender:', error);
      return <>{options.errorFallback || <ErrorState error={error as Error}>{null}</ErrorState>}</>;
    }
  };

  WrappedComponent.displayName = `withSafeRender(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};
